import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowLeft,
  Search,
  ShieldX,
  Car,
  FileText,
  Building2,
  AlertTriangle,
  Wallet,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import { getClients } from "../api";

import "../styles/expiredpolicies.css";

const ExpiredPolicies = () => {
  const [clients, setClients] =
    useState([]);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [showAllExpired, setShowAllExpired] =
    useState(false);

  const navigate = useNavigate();

  const expiredPoliciesFormatDate =
    (dateString) => {
      if (!dateString)
        return "N/A";

      const d = new Date(
        dateString
      );

      if (isNaN(d.getTime()))
        return "N/A";

      return d.toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    };

  const getEffectiveRenewalDate =
    (client) =>
      client.latest_next_renewal_date ||
      client.effective_next_renewal_date ||
      client.renewal_date ||
      null;

  useEffect(() => {
    const fetchData =
      async () => {
        const data =
          await getClients();

        const expired =
          data.filter(
            (client) => {
              const renewal =
                getEffectiveRenewalDate(
                  client
                );

              if (!renewal)
                return false;

              return (
                new Date(
                  renewal
                ) <
                new Date()
              );
            }
          );

        setClients(expired);
      };

    fetchData();
  }, []);

  const expiredPoliciesFilteredClients =
    useMemo(() => {
      return clients
        .filter(
          (client) =>
            client.name
              ?.toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              ) ||
            client.policy_number
              ?.toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              ) ||
            client.vehicle_number
              ?.toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              )
        )

        .sort((a, b) =>
          a.name.localeCompare(
            b.name
          )
        );
    }, [clients, searchTerm]);

  const expiredPoliciesDisplayedClients =
    searchTerm.trim()
      ? expiredPoliciesFilteredClients
      : showAllExpired
      ? expiredPoliciesFilteredClients
      : expiredPoliciesFilteredClients.slice(
          0,
          10
        );

  return (
    <div className="expired-policies-modern-page">

      {/* HERO */}

      <div className="expired-policies-modern-hero">

        <div className="expired-policies-modern-left">

          <div className="expired-policies-alert-pill">
            <ShieldX size={16} />
            Policy Expiration
          </div>

          <h1 className="expired-policies-heading">
            Expired
            Policies.
          </h1>

          <p className="expired-policies-description">
            Expired Polices that have not been renewed through RCS.
          </p>

          <div className="expired-policies-actions">

            <button
              className="expired-policies-back-button"
              onClick={() =>
                navigate(
                  "/insurance"
                )
              }
            >
              <ArrowLeft size={18} />
              Back to Insurance
            </button>
          </div>
        </div>

        {/* STATS */}

        <div className="expired-policies-stats-grid">

          <div className="expired-policies-stat-card">

            <div className="expired-policies-stat-icon red">
              <AlertTriangle size={22} />
            </div>

            <div>
              <span>
                Expired Policies
              </span>

              <h2>
                {
                  clients.length
                }
              </h2>
            </div>
          </div>

          <div className="expired-policies-stat-card">

            <div className="expired-policies-stat-icon orange">
              <Wallet size={22} />
            </div>

            <div>
              <span>
                Displayed
              </span>

              <h2>
                {
                  expiredPoliciesDisplayedClients.length
                }
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* TOOLBAR */}

      <div className="expired-policies-toolbar">

        <div className="expired-policies-search-box">

          <Search size={18} />

          <input
            type="text"
            placeholder="Search client, vehicle or policy number..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(
                e.target.value
              )
            }
          />
        </div>

        <div className="expired-policies-toolbar-right">

          <div className="expired-policies-results">
            {searchTerm.trim()
              ? `${expiredPoliciesFilteredClients.length} client(s) found`
              : showAllExpired
              ? `Showing all expired policies`
              : `Showing top 10 expired policies`}
          </div>

          {!searchTerm.trim() && (
            <button
              className="expired-policies-toggle-btn"
              onClick={() =>
                setShowAllExpired(
                  !showAllExpired
                )
              }
            >
              {showAllExpired
                ? "Collapse Table"
                : "View All"}
            </button>
          )}
        </div>
      </div>

      {/* TABLE */}

      <div className="expired-policies-table-wrapper">

        <table className="expired-policies-table">

          <thead>
            <tr>

              <th>
                Client
              </th>

              <th>
                Policy
              </th>

              <th>
                Vehicle
              </th>

              <th>
                Insurer
              </th>

              <th>
                Premium
              </th>

              <th>
                Expired Date
              </th>

              <th>
                Status
              </th>

            </tr>
          </thead>

          <tbody>

            {expiredPoliciesDisplayedClients.length >
            0 ? (
              expiredPoliciesDisplayedClients.map(
                (
                  client,
                  i
                ) => (
                  <tr
                    key={i}
                  >

                    {/* CLIENT */}

                    <td>

                      <div className="expired-policies-client-cell">

                        <div className="expired-policies-avatar">
                          {client.name
                            ?.charAt(
                              0
                            )
                            ?.toUpperCase()}
                        </div>

                        <div>
                          <h4>
                            {
                              client.name
                            }
                          </h4>

                          <span>
                            {
                              client.policy_type
                            }
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* POLICY */}

                    <td>

                      <div className="expired-policies-info-cell">

                        <FileText size={16} />

                        <span>
                          {
                            client.policy_number
                          }
                        </span>

                      </div>
                    </td>

                    {/* VEHICLE */}

                    <td>

                      <div className="expired-policies-info-cell">

                        <Car size={16} />

                        <span>
                          {
                            client.vehicle_number
                          }
                        </span>

                      </div>
                    </td>

                    {/* INSURER */}

                    <td>

                      <div className="expired-policies-info-cell">

                        <Building2 size={16} />

                        <span>
                          {
                            client.insurer
                          }
                        </span>

                      </div>
                    </td>

                    {/* PREMIUM */}

                    <td className="expired-policies-amount">
                      $
                      {parseFloat(
                        client.premium ||
                          0
                      ).toLocaleString()}
                    </td>

                    {/* DATE */}

                    <td>
                      {expiredPoliciesFormatDate(
                        getEffectiveRenewalDate(
                          client
                        )
                      )}
                    </td>

                    {/* STATUS */}

                    <td>

                      <span className="expired-policies-status-pill">
                        Expired
                      </span>

                    </td>

                  </tr>
                )
              )
            ) : (
              <tr>

                <td
                  colSpan="7"
                  className="expired-policies-empty-state"
                >

                  <ShieldX size={44} />

                  <h3>
                    No Expired
                    Policies
                  </h3>

                  <p>
                    No expired
                    insurance
                    records were
                    found.
                  </p>

                </td>

              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExpiredPolicies;